import { Avatar } from '../../../components/Avatar'
import { PageHeader } from '../../../components/PageHeader'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { useT } from '../../../i18n'

interface PlayerResult {
  id: string
  name: string
  isAi: boolean
  avatarUrl?: string | null
  totalYes: number
  totalNo: number
  courses: { text: string; yes: number; no: number }[]
}

interface Props {
  results: PlayerResult[]
  onPlayAgain: () => void
  onBackToHub: () => void
}

export function ResultsScreen({ results, onPlayAgain, onBackToHub }: Props) {
  const t = useT()
  const sorted = [...results].sort((a, b) => b.totalYes - a.totalYes)
  const winner = sorted[0]

  return (
    <div className="screen">
      <BackgroundTitle text="I'd Eat That" color="#B58615" />
      <PageHeader title={t('Results')} />

      <div className="px-5 py-5 space-y-5 overflow-y-auto pb-32 glass-panes">
        {/* Winner banner */}
        <div className="card-lg p-6 text-center bg-linear-to-br from-brand-gold/10 to-iet-orange/10
                        border border-brand-gold/30">
          <div className="text-4xl mb-3">🏆</div>
          <p className="text-sm text-brand-muted font-medium mb-1">{t('Most People Would Eat')}</p>
          <Avatar name={winner.name} avatarUrl={winner.avatarUrl} isAi={winner.isAi} size="lg" className="mx-auto mb-3" />
          <h2 className="font-display text-2xl font-bold text-brand-text">{winner.name}</h2>
          <p className="text-brand-primary font-semibold text-lg mt-1">
            {winner.totalYes} would eat • {winner.totalNo} wouldn't
          </p>
        </div>

        {/* All results */}
        <div>
          <h3 className="font-semibold text-brand-text mb-3">{t('All Results')}</h3>
          <div className="space-y-3">
            {sorted.map((player, rank) => (
              <div key={player.id} className="card overflow-hidden">
                <div className="flex items-center gap-3 p-4 border-b border-brand-border">
                  <span className="font-display text-xl font-bold text-brand-muted w-6 text-center">
                    {rank + 1}
                  </span>
                  <Avatar name={player.name} avatarUrl={player.avatarUrl} isAi={player.isAi} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-brand-text">{player.name}</span>
                      {player.isAi && <span className="ai-badge">{t('🤖 AI')}</span>}
                    </div>
                    <p className="text-xs text-brand-muted">
                      {player.totalYes} ✅ · {player.totalNo} 🙅
                    </p>
                  </div>
                  <div className="score-ring w-12 h-12 text-base">
                    {player.totalYes}
                  </div>
                </div>

                {/* Course breakdown */}
                <div className="divide-y divide-brand-border">
                  {player.courses.map((course, i) => (
                    <div key={i} className="px-4 py-3 flex gap-3">
                      <span className="text-lg shrink-0">
                        {['🥗', '🍽️', '🍮'][i]}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-brand-muted font-medium mb-0.5">
                          {['Appetizer', 'Main', 'Dessert'][i]}
                        </p>
                        <p className="text-sm text-brand-text line-clamp-2">"{course.text}"</p>
                        <div className="flex gap-3 mt-1 text-xs text-brand-muted">
                          <span>✅ {course.yes}</span>
                          <span>🙅 {course.no}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="floating-actions space-y-3">
        <button onClick={onPlayAgain} className="btn-primary">{t('Play Again')}</button>
        <button onClick={onBackToHub} className="btn-ghost w-full text-center">
          {t('Back to Games')}
        </button>
      </div>
    </div>
  )
}
